import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowUp,
  Check,
  Download,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import {
  deleteBrochure,
  fetchAllBrochuresAdmin,
  reorderBrochures,
  triggerBrochureFileDownload,
  updateBrochure,
  uploadBrochures,
} from "@/lib/api";
import { useSiteContent } from "@/lib/SiteContent";

const PDF_MIME = "application/pdf";
const MAX_PDF_BYTES = 50 * 1024 * 1024;

const formatSize = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

// =====================================================================
// Upload zone — multi-file picker + drag-and-drop
// =====================================================================
const UploadZone = ({ onFilesPicked, uploading }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const validateAndPick = useCallback(
    (fileList) => {
      const all = Array.from(fileList || []);
      if (!all.length) return;
      const accepted = [];
      const rejected = [];
      for (const f of all) {
        const isPdf =
          (f.type || "").toLowerCase() === PDF_MIME ||
          (f.name || "").toLowerCase().endsWith(".pdf");
        if (!isPdf) {
          rejected.push(`${f.name} — not a PDF`);
          continue;
        }
        if (f.size > MAX_PDF_BYTES) {
          rejected.push(`${f.name} — exceeds 50 MB limit`);
          continue;
        }
        accepted.push(f);
      }
      if (rejected.length) {
        toast.error(`${rejected.length} file(s) skipped`, {
          description: rejected.slice(0, 3).join("\n") + (rejected.length > 3 ? "\n…" : ""),
        });
      }
      if (accepted.length) {
        onFilesPicked(accepted);
      }
    },
    [onFilesPicked]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    validateAndPick(e.dataTransfer?.files);
  };

  const onPick = (e) => {
    validateAndPick(e.target.files);
    e.target.value = ""; // allow re-picking the same file
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      data-testid="brochure-upload-zone"
      className={`relative glass-card neon-border-emerald rounded-[18px] border-2 border-dashed p-8 md:p-10 transition-all ${
        dragOver
          ? "border-[color:var(--sp-gold)] bg-[color:var(--sp-gold-soft)]"
          : "border-white/15 hover:border-white/30"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        className="hidden"
        onChange={onPick}
        data-testid="brochure-upload-input"
      />
      <div className="flex flex-col items-center text-center gap-3">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[color:var(--sp-gold-soft)] text-[color:var(--sp-gold)]">
          {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <Upload className="h-6 w-6" />}
        </div>
        <p className="font-display text-2xl text-white">
          {uploading ? "Uploading…" : "Drop PDFs here or click to browse"}
        </p>
        <p className="text-sm text-white/55 max-w-md">
          Upload one or many brochure PDFs at once. Each file can be up to 50&nbsp;MB.
          Titles auto-populate from the filename and can be edited below.
        </p>
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          data-testid="brochure-upload-button"
          className="btn-gold inline-flex items-center justify-center gap-2 px-6 py-2.5 text-[11px] tracking-[0.22em] uppercase mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4" /> Choose PDFs
        </button>
      </div>
    </div>
  );
};

// =====================================================================
// Row — single brochure with inline edit / reorder / delete / download
// =====================================================================
const BrochureRow = ({
  brochure,
  index,
  total,
  saving,
  onSave,
  onMove,
  onDelete,
  onDownload,
}) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(brochure.title || "");
  const [description, setDescription] = useState(brochure.description || "");

  const cancel = () => {
    setTitle(brochure.title || "");
    setDescription(brochure.description || "");
    setEditing(false);
  };

  const save = async () => {
    if (!title.trim()) {
      toast.error("Title is required.");
      return;
    }
    const ok = await onSave(brochure.id, {
      title: title.trim(),
      description: description.trim() || null,
    });
    if (ok) setEditing(false);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.18 }}
      className="glass-card rounded-[16px] border border-white/10 p-5"
      data-testid={`brochure-row-${brochure.id}`}
    >
      <div className="flex flex-col md:flex-row md:items-start gap-5">
        {/* Left — icon + meta */}
        <div className="flex items-center gap-4 md:w-12 md:flex-col md:items-center">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[color:rgba(212,175,55,0.12)] text-[color:var(--sp-gold)] border border-[color:rgba(212,175,55,0.32)]">
            <FileText className="h-5 w-5" />
          </div>
        </div>

        {/* Middle — title + description (editable) */}
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="space-y-3">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid={`brochure-title-input-${brochure.id}`}
                placeholder="Brochure title (shown to visitors)"
                className="input-luxe w-full px-4 py-2.5 text-sm"
              />
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                data-testid={`brochure-description-input-${brochure.id}`}
                placeholder="Short description (optional) — e.g. 66-acre township master plan."
                className="input-luxe w-full px-4 py-2.5 text-sm resize-y"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid={`brochure-save-${brochure.id}`}
                  className="btn-gold inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase disabled:opacity-60"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                </button>
                <button
                  onClick={cancel}
                  data-testid={`brochure-cancel-${brochure.id}`}
                  className="btn-ghost inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase"
                >
                  <X className="h-3.5 w-3.5" /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-display text-xl text-white" data-testid={`brochure-title-${brochure.id}`}>
                  {brochure.title || "Untitled brochure"}
                </p>
                <span className="text-[10px] tracking-[0.22em] uppercase text-white/45 bg-white/5 rounded-full px-2 py-1">
                  #{index + 1}
                </span>
              </div>
              {brochure.description && (
                <p className="mt-2 text-sm text-white/65" data-testid={`brochure-description-${brochure.id}`}>
                  {brochure.description}
                </p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-white/45">
                <span>{brochure.originalName || brochure.filename}</span>
                <span>·</span>
                <span>{formatSize(brochure.sizeBytes)}</span>
                <span>·</span>
                <span>Added {formatDate(brochure.createdAt)}</span>
              </div>
            </>
          )}
        </div>

        {/* Right — actions */}
        {!editing && (
          <div className="flex flex-wrap md:flex-col items-stretch gap-2 md:w-44">
            <button
              onClick={() => onDownload(brochure)}
              data-testid={`brochure-download-${brochure.id}`}
              className="btn-ghost inline-flex items-center justify-center gap-2 px-3 py-2 text-[10px] tracking-[0.22em] uppercase"
            >
              <Download className="h-3.5 w-3.5" /> Preview
            </button>
            <button
              onClick={() => setEditing(true)}
              data-testid={`brochure-edit-${brochure.id}`}
              className="btn-ghost inline-flex items-center justify-center gap-2 px-3 py-2 text-[10px] tracking-[0.22em] uppercase"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => onMove(index, -1)}
                disabled={index === 0 || saving}
                data-testid={`brochure-move-up-${brochure.id}`}
                className="btn-ghost inline-flex items-center justify-center gap-1 px-2 py-2 text-[10px] tracking-[0.18em] uppercase disabled:opacity-40 disabled:cursor-not-allowed"
                title="Move up"
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => onMove(index, +1)}
                disabled={index === total - 1 || saving}
                data-testid={`brochure-move-down-${brochure.id}`}
                className="btn-ghost inline-flex items-center justify-center gap-1 px-2 py-2 text-[10px] tracking-[0.18em] uppercase disabled:opacity-40 disabled:cursor-not-allowed"
                title="Move down"
              >
                <ArrowDown className="h-3.5 w-3.5" />
              </button>
            </div>
            <button
              onClick={() => onDelete(brochure)}
              data-testid={`brochure-delete-${brochure.id}`}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 text-[10px] tracking-[0.22em] uppercase rounded-md border border-red-500/35 text-red-300 hover:bg-red-500/10 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// =====================================================================
// Page
// =====================================================================
const AdminBrochuresPage = () => {
  const { refresh } = useSiteContent();
  const [brochures, setBrochures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchAllBrochuresAdmin();
      setBrochures(list);
    } catch (err) {
      toast.error("Could not load brochures", {
        description: err?.response?.data?.detail || err?.message || "Please retry.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onFilesPicked = async (files) => {
    setUploading(true);
    try {
      const created = await uploadBrochures(files);
      toast.success(`${created.length} PDF${created.length === 1 ? "" : "s"} uploaded`, {
        description: "Edit the title and description for each below.",
      });
      await load();
      refresh(); // refresh the public-facing context too
    } catch (err) {
      toast.error("Upload failed", {
        description: err?.response?.data?.detail || err?.message || "Please retry.",
      });
    } finally {
      setUploading(false);
    }
  };

  const onSave = async (id, payload) => {
    setSavingId(id);
    try {
      const updated = await updateBrochure(id, payload);
      setBrochures((prev) => prev.map((b) => (b.id === id ? updated : b)));
      toast.success("Saved.");
      refresh();
      return true;
    } catch (err) {
      toast.error("Could not save", {
        description: err?.response?.data?.detail || err?.message,
      });
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const onMove = async (index, delta) => {
    const next = [...brochures];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    // Optimistic UI
    setBrochures(next.map((b, i) => ({ ...b, order: i })));
    try {
      const fresh = await reorderBrochures(next.map((b, i) => ({ id: b.id, order: i })));
      setBrochures(fresh);
      refresh();
    } catch (err) {
      toast.error("Could not save the new order", {
        description: err?.response?.data?.detail || err?.message,
      });
      load();
    }
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    const { id, title } = confirmDelete;
    setConfirmDelete(null);
    try {
      await deleteBrochure(id);
      setBrochures((prev) => prev.filter((b) => b.id !== id));
      toast.success(`Deleted "${title}".`);
      refresh();
    } catch (err) {
      toast.error("Could not delete", {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  const onDownload = async (brochure) => {
    await triggerBrochureFileDownload(brochure);
  };

  return (
    <AdminLayout
      eyebrow="Brochure Library"
      title="Brochures."
      description="Curate the PDFs visitors receive when they submit the brochure download form on the Projects page. Upload multiple files at once; reorder, rename and remove as needed."
    >
      <div className="space-y-6">
        <UploadZone onFilesPicked={onFilesPicked} uploading={uploading} />

        <div className="flex items-center justify-between">
          <p className="text-[11px] tracking-[0.28em] uppercase text-[color:var(--sp-gold)]">
            <span className="gold-divider" /> Uploaded Brochures
          </p>
          <p className="text-xs text-white/45" data-testid="brochures-count">
            {loading ? "Loading…" : `${brochures.length} file${brochures.length === 1 ? "" : "s"}`}
          </p>
        </div>

        {loading && (
          <div className="glass-card rounded-[16px] border border-white/10 p-10 text-center">
            <Loader2 className="h-5 w-5 mx-auto animate-spin text-[color:var(--sp-gold)]" />
            <p className="text-sm text-white/55 mt-3">Loading brochures…</p>
          </div>
        )}

        {!loading && brochures.length === 0 && (
          <div
            data-testid="brochures-empty-state"
            className="glass-card rounded-[16px] border border-white/10 p-10 text-center"
          >
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white/5 text-white/40">
              <FileText className="h-6 w-6" />
            </div>
            <p className="font-display text-2xl text-white mt-4">No brochures uploaded yet.</p>
            <p className="text-sm text-white/55 mt-2 max-w-md mx-auto">
              Until you upload at least one PDF, the brochure download form on the Projects page will
              show your &ldquo;we&rsquo;ll email it shortly&rdquo; fallback message to visitors. Leads are still captured.
            </p>
          </div>
        )}

        <div className="space-y-3">
          <AnimatePresence initial={false}>
            {brochures.map((b, i) => (
              <BrochureRow
                key={b.id}
                brochure={b}
                index={i}
                total={brochures.length}
                saving={savingId === b.id}
                onSave={onSave}
                onMove={onMove}
                onDelete={() => setConfirmDelete({ id: b.id, title: b.title })}
                onDownload={onDownload}
              />
            ))}
          </AnimatePresence>
        </div>

        {/* Helpful link to public preview */}
        {brochures.length > 0 && (
          <div className="rounded-[14px] border border-[color:rgba(212,175,55,0.25)] bg-[color:var(--sp-gold-soft)] px-5 py-4 text-sm text-white/75 flex items-start gap-3">
            <Check className="mt-0.5 h-4 w-4 text-[color:var(--sp-gold)] shrink-0" />
            <span>
              Visitors who submit the brochure form will see <strong>all {brochures.length}</strong>{" "}
              PDF{brochures.length === 1 ? "" : "s"} in a list with individual download buttons, in
              the order shown above.
            </span>
          </div>
        )}
      </div>

      {/* Confirmation dialog */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
            onClick={() => setConfirmDelete(null)}
            data-testid="brochure-delete-modal"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={(e) => e.stopPropagation()}
              className="glass-strong rounded-2xl border border-white/15 p-6 md:p-7 max-w-md w-full"
            >
              <p className="text-[10px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
                <span className="gold-divider" /> Confirm delete
              </p>
              <h3 className="font-display text-2xl mt-2 text-white">Remove brochure?</h3>
              <p className="text-sm text-white/65 mt-3">
                &ldquo;<span className="text-white">{confirmDelete.title}</span>&rdquo; will be permanently removed
                from the site and the PDF file deleted from storage. This cannot be undone.
              </p>
              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  onClick={() => setConfirmDelete(null)}
                  data-testid="brochure-delete-cancel"
                  className="btn-ghost inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase"
                >
                  Cancel
                </button>
                <button
                  onClick={onDelete}
                  data-testid="brochure-delete-confirm"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2 text-[11px] tracking-[0.22em] uppercase rounded-md bg-red-500/90 hover:bg-red-500 text-white transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Yes, delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
};

export default AdminBrochuresPage;
